import api from "./api";

interface User {
  id: number;
  name: string | null;
  email: string;
  age: number | null;
  weight: number | null;
  height: number | null;
  gender: string | null;
  typeOfSubscription: string | null;
  isBlocked: boolean;
  role: string | null;
  phoneNumber: string | null;
  surname: string | null;
  createdAt: string;
  questionnaireAnswers: any[];
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  currentPage: number;
  totalPages: number;
}

export const getUsers = async (
  page: number = 1,
  limit: number = 10,
  name: string[] = [],
  email: string[] = [],
  role: string[] = []
): Promise<PaginatedResponse<User>> => {
  try {
    const params = {
      page,
      limit,
      ...(name.length > 0 && { name }),
      ...(email.length > 0 && { email }),
      ...(role.length > 0 && { role }),
    };

    const response = await api.get<PaginatedResponse<User>>(`/auth/users`, {
      params,
      timeout: 0,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      },
    });

    return {
      data: response.data.data || [],
      total: response.data.total || 0,
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
    };
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const getSubscriptionCount = async () => {
  try {
    const response = await api.get("/auth/subscriptions/stats");

    return response.data;
  } catch (error) {
    console.error(`Error count of subscription}:`, error);
    return [];
  }
};

export const searchUsers = async (
  searchType: "name" | "email" | "role",
  query: string = ""
): Promise<string[]> => {
  try {
    const params: Record<string, string> = {};
    params[searchType] = query;

    const response = await api.get<PaginatedResponse<User>>("/auth/users", {
      params: {
        ...params,
        limit: 10,
      },
    });

    const uniqueValues = Array.from(
      new Set(
        response.data.data
          .map((user) => user[searchType])
          .filter((value): value is string => value !== null)
      )
    );

    return uniqueValues;
  } catch (error) {
    console.error(`Error searching ${searchType}:`, error);
    return [];
  }
};
export const updateUserStatus = async (
  userId: number,
  isBlocked: boolean
): Promise<User> => {
  const token = localStorage.getItem("access_token");
  if (!token) throw new Error("Access token is missing");

  const response = await api.post<User>(
    `/auth/block-unblock`,
    { isBlocked, userId },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  console.log(response.data);

  return response.data;
};

export interface UserDetail {
  id: number;
  name: string | null;
  surname: string | null;
  email: string;
  phoneNumber: string | null;
  gender: string | null;
  age: number | null;
  role: string | null;
  isVerified: boolean;
  isBlocked: boolean;
  typeOfSubscription: string | null;
  currentSubscription?: { type: string; purchaseDate: string } | null;
  subscriptionStatus: string | null;
  subscriptionExpiresAt: string | null;
  isPremium: boolean;
  premiumEndDate: string | null;
  premiumBy: string | null;
  lastLoginAt: string | null;
  height: string | null;
  weight: string | null;
  minutesForTrain: string | null;
  bodyType: string | null;
  createdAt: string;
  updatedAt: string;
  // questionnaireAnswers intentionally omitted — GET /auth/users/:id does not
  // eager-load this relation, unlike the list endpoint above. Do not assume
  // it is populated here.
}

export const getUserById = async (id: number | string): Promise<UserDetail> => {
  try {
    const response = await api.get<UserDetail>(`/auth/users/${id}`, {
      timeout: 0,
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${id}:`, error);
    throw error;
  }
};

export const grantSubscriptionService = async (
  userId: number,
  isPremium: boolean,
  plan: "monthly" | "yearly" | "inactive"
): Promise<User> => {
  const token = localStorage.getItem("access_token");
  if (!token) throw new Error("Access token is missing");

  const response = await api.post<User>(
    `/admin/manage-premium/${userId}`,
    { isPremium, plan },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  console.log(response.data);

  return response.data;
};
